import { Link } from "react-router-dom";
import GuidePage from "@/components/marketing/GuidePage";
import { BRAND, LAB_REPORT } from "@/content/brand";

const UPDATED = "2026-09-28";

// Tables scroll inside their own box on narrow phones, never the page.
export function Table({ head, rows, caption }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} scope="col">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[0]}>
              {r.map((c, i) => (i === 0 ? <th key={i} scope="row">{c}</th> : <td key={i}>{c}</td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const labRow = (param) => LAB_REPORT.rows.find((r) => r.param === param);
const sat = labRow("Saturated fat");

/* ------------------------------------------------------------------ */
/* Cold pressed vs refined                                             */
/* ------------------------------------------------------------------ */

export function ColdPressedVsRefined() {
  return (
    <GuidePage
      path="/cold-pressed-vs-refined-coconut-oil"
      seoTitle="Cold Pressed vs Refined (RBD) Coconut Oil"
      seoDescription="Cold pressed vs refined (RBD) vs wood pressed coconut oil: how each is made, how they taste, what refining removes, why prices differ and which to buy."
      eyebrow="Buying guide"
      title="Cold pressed or refined coconut oil: what is the real difference?"
      tldr="Cold pressed coconut oil is pressed and filtered, and nothing more, so it keeps the smell and taste of coconut. Refined coconut oil, often labelled RBD, is refined, bleached and deodorised after extraction, which makes it neutral in smell and colour and strips out some natural compounds. Wood pressed (chekku) oil is a kind of cold pressing done in a traditional wooden press. For South Indian cooking, hair and skin, most families prefer unrefined oil."
      updated={UPDATED}
      testid="guide-cold-vs-refined"
      sections={[
        {
          id: "three-kinds",
          heading: "What are the three kinds of coconut oil on the shelf?",
          body: (
            <>
              <p>
                Walk down any supermarket aisle in India and you will see three words on coconut oil jars: cold pressed, wood pressed and refined. They sound like marketing, but they describe real differences in how the oil is made, and those differences change what you smell, taste and pay.
              </p>
              <ul>
                <li><strong>Cold pressed.</strong> Dried coconut kernel (copra) or coconut is pressed mechanically and the oil is filtered. It is not refined, bleached or deodorised.</li>
                <li><strong>Wood pressed, chekku, kachi ghani or marachekku.</strong> A kind of cold pressing where a wooden pestle turns slowly inside a wooden or stone mortar. It is the older village method and is popular again.</li>
                <li><strong>Refined or RBD.</strong> Oil that has been extracted and then Refined, Bleached and Deodorised. The result is a pale, almost odourless oil.</li>
              </ul>
              <p>
                There is also a fourth label, virgin coconut oil, which has its own FSSAI standard (added in 2017). In the trade, virgin usually means oil from fresh kernel rather than dried copra. We sell Karijeeva as cold pressed coconut oil, not as virgin coconut oil.
              </p>
            </>
          ),
        },
        {
          id: "how-made",
          heading: "How is each one made?",
          body: (
            <>
              <h3>Cold pressed</h3>
              <p>
                The kernel is pressed in a mechanical press and the oil that comes out is left to settle and then filtered. That is the whole process. Because nothing is taken out after pressing, the oil keeps the colour, aroma and minor compounds that came from the coconut.
              </p>
              <h3>Wood pressed (chekku)</h3>
              <p>
                In a chekku, a heavy wooden rotor turns slowly in a hollow block, crushing the kernel. The low speed is the point of the method. Brands use the words chekku, ghani and wood pressed in different ways, so if the method matters to you, ask the seller which press they use.
              </p>
              <h3>Refined (RBD)</h3>
              <p>
                Refined oil goes through extra steps after extraction. Refining treats the oil to remove free fatty acids, bleaching uses earths or clays to remove colour, and deodorising uses high heat and steam to strip away smell and taste. What is left is a neutral oil with a long, uniform shelf life.
              </p>
            </>
          ),
        },
        {
          id: "what-refining-removes",
          heading: "What does refining remove from coconut oil?",
          body: (
            <>
              <p>
                The obvious loss is aroma and flavour, since that is exactly what deodorising is designed to remove. Refined oil will not give your coconut chutney tadka or your avial the smell your family expects.
              </p>
              <p>
                Refining also lowers some of the natural compounds in the oil. A 2009 study by Marina and colleagues measured total phenolics of 7.78 to 29.18 mg GAE per 100 g in virgin coconut oil samples, against 6.14 mg GAE per 100 g in refined (RBD) coconut oil. That study compared virgin oil from fresh kernel with RBD oil, so it tells you refining strips these compounds, not what any one cold pressed brand contains.
              </p>
              <p className="note">
                What refining does not change much is the fat itself. Refined and unrefined coconut oil are both mostly saturated fat. Our latest lab report shows {sat.value} {sat.unit} of saturated fat per 100 g. Unrefined does not mean you can use more of it.
              </p>
            </>
          ),
        },
        {
          id: "compare-table",
          heading: "Cold pressed vs wood pressed vs refined: side by side",
          body: (
            <>
              <Table
                caption="Comparison of cold pressed, wood pressed and refined coconut oil"
                head={["", "Cold pressed", "Wood pressed (chekku)", "Refined (RBD)"]}
                rows={[
                  ["How the oil is taken out", "Mechanical press, then filtered", "Slow wooden press, then filtered", "Extracted, then refined, bleached and deodorised"],
                  ["Aroma and taste", "Clear coconut aroma", "Clear coconut aroma", "Neutral, little or no smell"],
                  ["Colour", "Clear to pale", "Clear to pale", "Very pale, uniform"],
                  ["Natural phenolics", "Kept, since nothing is removed", "Kept, since nothing is removed", "Reduced by refining"],
                  ["Saturated fat", "High", "High", "High"],
                  ["Typical price", "Higher", "Often highest", "Lowest"],
                  ["Best for", "South Indian cooking, hair, skin, baby massage", "Same as cold pressed", "Neutral baking, where you do not want coconut flavour"],
                ]}
              />
              <p>
                Look at the saturated fat row. The kind of oil changes aroma, colour and minor compounds, not the basic nutrition. Whichever you pick, use it as one oil in your kitchen, not the only one.
              </p>
            </>
          ),
        },
        {
          id: "price",
          heading: "Why does cold pressed coconut oil cost more?",
          body: (
            <>
              <p>
                This is the question most people are really asking. A litre of refined coconut oil is usually cheaper than a litre of cold pressed oil, and the reasons are not a mystery.
              </p>
              <ul>
                <li><strong>Raw material.</strong> Unrefined oil can only be as good as the copra it starts from, because there is no refining step to hide flaws. Good copra is not cheap, and its price moves with every season.</li>
                <li><strong>Scale.</strong> Refined oil is made in large plants where cost per litre falls. Cold pressed oil is usually made in smaller batches.</li>
                <li><strong>Testing.</strong> A brand that sends batches to a lab pays for every one of those tests.</li>
              </ul>
              <p>
                At Karijeeva, all three apply. The coconuts come from our family farms in Tiptur and on the Uttara Kannada coast from Gokarna to Shirali, we press in small batches, and every batch goes to an independent food testing lab. We have tested {BRAND.batchesTested} batches so far. That is where your money goes.
              </p>
            </>
          ),
        },
        {
          id: "which-to-buy",
          heading: "Which coconut oil is best for cooking, hair and skin?",
          body: (
            <>
              <p>
                Buy for the job, not the label. Here is how we would choose.
              </p>
              <ul>
                <li><strong>South Indian cooking</strong> (tadka, thoran, poriyal, avial, appam): cold pressed or wood pressed. The coconut aroma is part of the dish. See our <Link to="/cooking-with-coconut-oil">cooking guide</Link>.</li>
                <li><strong>Baking where you want no coconut taste:</strong> refined oil is a fair choice, or use butter or another oil.</li>
                <li><strong>Hair champi, dry skin and baby massage:</strong> a single ingredient, unrefined oil with nothing added. Read the <Link to="/coconut-oil-for-hair">hair guide</Link> and the <Link to="/coconut-oil-for-baby-massage">baby massage guide</Link>.</li>
                <li><strong>Deep frying:</strong> coconut oil is not the best fit, cold pressed or refined. Use it for tempering and shallow frying instead.</li>
              </ul>
              <p>
                Whatever you buy, check that it is actually coconut oil. Our guide on <Link to="/how-to-check-coconut-oil-purity">how to check coconut oil purity</Link> walks you through the FSSAI fridge test and what to read on the label.
              </p>
            </>
          ),
        },
        {
          id: "karijeeva",
          heading: "Where does Karijeeva fit?",
          body: (
            <>
              <p>
                Karijeeva is cold pressed coconut oil with one ingredient: coconut. It is not refined, bleached or deodorised, and it is never blended with other oils or given added colour or aroma. Every batch is tested at an independent food testing lab, and you can read our latest report on the <Link to="/lab-reports">lab reports page</Link>. Our FSSAI licence is {BRAND.fssai}.
              </p>
              <p>
                If you have been buying refined oil for everyday cooking and want the coconut flavour back, or you are not sure what is in your current jar, try one jar of ours alongside the oils you already use. You can <Link to="/products">see sizes and prices here</Link>. Unopened jars can be returned within 7 days.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        { q: "Is cold pressed coconut oil better than refined coconut oil?", a: "For flavour and for keeping the natural compounds of the coconut, yes, because refining, bleaching and deodorising remove aroma and some phenolics. For basic nutrition they are similar, since both are mostly saturated fat. Choose cold pressed when you want coconut taste or a single ingredient oil for hair and skin." },
        { q: "What does RBD coconut oil mean?", a: "RBD stands for refined, bleached and deodorised. The oil is treated after extraction to remove free fatty acids, colour and smell. The result is a neutral oil that does not taste of coconut." },
        { q: "Is wood pressed coconut oil the same as cold pressed?", a: "Wood pressed (chekku) is a kind of cold pressing that uses a slow wooden press. Both leave the oil unrefined. Brands use these words loosely, so ask the seller how their oil is pressed if the method matters to you." },
        { q: "Is cold pressed the same as virgin coconut oil?", a: "Not always. FSSAI has a separate standard for virgin coconut oil, and in the trade virgin usually means oil from fresh kernel. Cold pressed oil is often pressed from dried copra. Karijeeva is sold as cold pressed coconut oil, not virgin." },
        { q: "Which coconut oil is best for cooking Indian food?", a: "For South Indian dishes where the coconut aroma matters, choose an unrefined cold pressed or wood pressed oil. Use it for tempering and shallow frying at medium heat, and rotate it with other oils as ICMR NIN suggests." },
        { q: "Why is my cold pressed coconut oil more expensive?", a: "Unrefined oil depends entirely on good raw material, it is usually made in smaller batches, and careful brands pay for lab testing. Refined oil is made at a much larger scale, which lowers the cost of each litre." },
      ]}
      sources={[
        { label: "Marina et al. 2009, phenolic content of virgin and refined coconut oil, Int J Food Sci Nutr", url: "https://www.tandfonline.com/doi/full/10.1080/09637480802549127" },
        { label: "FSSAI Food Products Standards, Chapter 2.2 Fats, oils and fat emulsions (Version 5, 2025)", url: "https://www.fssai.gov.in/upload/uploadfiles/files/Chapter%202_2_Fats_oils%20and%20fat%20emulsions.pdf" },
        { label: "FSSAI gazette notification adding the Virgin Coconut Oil standard, October 2017", url: "https://fssai.gov.in/upload/uploadfiles/files/Gazette_Notification_Oils_Fat_20_10_2017.pdf.pdf" },
        { label: "Emissions of volatile aldehydes from heated cooking oils, Food Chemistry", url: "https://www.sciencedirect.com/science/article/abs/pii/S0308814609011303" },
        { label: "ICMR NIN FAQs on using a combination of oils", url: "https://www.nin.res.in/faqs.html" },
      ]}
      related={[
        { label: "How to check coconut oil purity", to: "/how-to-check-coconut-oil-purity" },
        { label: "Cooking with coconut oil", to: "/cooking-with-coconut-oil" },
        { label: "Cold pressed coconut oil benefits", to: "/cold-pressed-coconut-oil-benefits" },
        { label: "Our lab report", to: "/lab-reports" },
      ]}
    />
  );
}

/* ------------------------------------------------------------------ */
/* How to check purity                                                 */
/* ------------------------------------------------------------------ */

export function CheckPurity() {
  return (
    <GuidePage
      path="/how-to-check-coconut-oil-purity"
      seoTitle="How to Check Coconut Oil Purity at Home"
      seoDescription="How to check the purity of coconut oil: the FSSAI fridge test step by step, how adulterated oil looks, what to read on a label and how to read lab reports."
      eyebrow="Buying guide"
      title="How to check if your coconut oil is pure"
      tldr="Put a little coconut oil in a clear glass and keep it in the fridge (not the freezer) for 30 minutes. Pure coconut oil sets solid, while other liquid oils mixed into it stay as a separate liquid layer. This FSSAI home test catches crude mixing only, so also check the FSSAI licence and batch number on the label, and ask the brand for a lab report."
      updated={UPDATED}
      testid="guide-purity"
      sections={[
        {
          id: "why-worry",
          heading: "Is coconut oil adulteration really a problem in India?",
          body: (
            <>
              <p>
                Yes, and it is not a rumour. In 2018 alone, the Commissioner of Food Safety in Kerala banned 45 coconut oil brands in May, 51 in June and 74 in December. In 2019, FSSAI acted against 14 coconut oil brands. Many of the banned names were chosen to look like well known brands, which is how ordinary buyers get caught.
              </p>
              <p>
                Coconut oil costs more than most other edible oils, so there is money to be made by stretching it. Reports from Kerala describe cheaper oils such as palm, palm kernel and sunflower oil being mixed in, and mention mineral oil (liquid paraffin) as well. The FSSAI standard for coconut oil says it must be free from added colour, added flavour and mineral oil.
              </p>
            </>
          ),
        },
        {
          id: "fridge-test",
          heading: "How do you do the coconut oil fridge test?",
          body: (
            <>
              <p>
                FSSAI's DART (Detect Adulteration with Rapid Test) programme lists a simple home test for coconut oil. You need a clear glass, a spoon and a fridge.
              </p>
              <Table
                caption="FSSAI DART fridge test for coconut oil, step by step"
                head={["Step", "What to do", "Why"]}
                rows={[
                  ["1", "Pour a few spoons of the oil into a clean, dry, clear glass or small bowl.", "A clear glass lets you see layers."],
                  ["2", "If the oil is already solid, warm the glass in a bowl of warm water until fully liquid.", "You need to start with clear liquid oil."],
                  ["3", "Place the glass in the refrigerator, not the freezer.", "The freezer is cold enough to set other oils too."],
                  ["4", "Leave it for 30 minutes.", "This is the time FSSAI gives."],
                  ["5", "Take it out and look at it straight away.", "Coconut oil starts melting again at room temperature."],
                  ["6", "Solid all the way through: no sign of liquid oil mixed in. A separate liquid layer: another oil is likely mixed in.", "Most common cooking oils stay liquid at fridge temperature."],
                ]}
              />
              <p className="note">
                Please do this test on any coconut oil you buy, including ours. We would rather you check than trust a label, including our own.
              </p>
            </>
          ),
        },
        {
          id: "limits",
          heading: "What the fridge test cannot tell you",
          body: (
            <>
              <p>
                The fridge test is a good first check, but it has blind spots. Palm kernel oil also sets solid when cold, so coconut oil mixed with it can pass. Very small amounts of another oil may not form a layer you can see. And the test says nothing about how the oil was made, whether it has gone rancid, or how much moisture it carries.
              </p>
              <p>
                Use your nose too. Unrefined coconut oil smells of coconut. A sharp, sour or stale smell suggests the oil is old or poorly stored. A total lack of smell in an oil sold as cold pressed is worth a question to the seller.
              </p>
            </>
          ),
        },
        {
          id: "label",
          heading: "What should you read on a coconut oil label?",
          body: (
            <>
              <ul>
                <li><strong>FSSAI licence number.</strong> Every packed food sold in India must carry it. Ours is {BRAND.fssai}. You can look up a licence number on the FSSAI website.</li>
                <li><strong>Batch or lot number and date of packing.</strong> A batch number lets the brand trace a jar back to a specific production run and its lab test. No batch number is a red flag.</li>
                <li><strong>The words refined, RBD or blended.</strong> Any of these means the oil is not a single ingredient unrefined oil. Our guide to <Link to="/cold-pressed-vs-refined-coconut-oil">cold pressed vs refined coconut oil</Link> explains the difference.</li>
                <li><strong>Ingredients.</strong> Pure coconut oil has one: coconut oil. Anything else, including added flavour or colour, tells you it is something more.</li>
                <li><strong>Words FSSAI does not allow on edible oil,</strong> such as "Cholesterol Friendly", "Anti Cholesterol" or "Soothing to Heart". A brand using them is ignoring labelling rules.</li>
                <li><strong>Brand name and manufacturer address.</strong> Be wary of names that closely copy a famous brand. That trick showed up again and again in the Kerala bans.</li>
              </ul>
            </>
          ),
        },
        {
          id: "lab-report",
          heading: "How to read a coconut oil lab report",
          body: (
            <>
              <p>
                The strongest check is a report from an accredited lab. It is fair to ask any brand for one. Here is what the common quality parameters mean in plain words.
              </p>
              <Table
                caption="Common coconut oil quality parameters in plain words"
                head={["Parameter", "What it tells you"]}
                rows={[
                  ["Acid value or free fatty acids (FFA)", "How much the oil has started to break down. Lower is better. It rises with poor copra, moisture and age."],
                  ["Peroxide value", "Early signs of oxidation, the process that makes oil go rancid. Lower means fresher tasting oil."],
                  ["Moisture", "Water left in the oil. Too much water shortens shelf life."],
                  ["Iodine value", "A fingerprint of the fat type. Coconut oil has a low iodine value, and the FSSAI standard sets it at 7.5 to 10. A higher number can point to other oils mixed in."],
                  ["Saponification value", "Another fingerprint. FSSAI sets it at not less than 250 for coconut oil, and many other oils come out lower."],
                  ["Fatty acid profile", "The share of each fat. Codex puts lauric acid in coconut oil at 45.1 to 53.2% of total fatty acids."],
                ]}
              />
              <p>
                Our latest reports come from an independent food testing lab. The quality report checks the sample against the FSSAI standard for coconut oil: butyro refractometer reading, saponification value, iodine value, unsaponifiable matter, acid value (4.7 against a limit of 6.0) and the argemone oil test. It met the standard on every one. A second report gives the nutrition panel per 100 g. It does not include peroxide value, and we would rather tell you that than let you assume otherwise. You can read every number on our <Link to="/lab-reports">lab reports page</Link>, and if you have a question about your batch, <Link to="/contact">write to us</Link> with the batch number.
              </p>
            </>
          ),
        },
        {
          id: "switch",
          heading: "What to do if your oil fails",
          body: (
            <>
              <p>
                If your oil splits into layers in the fridge, stop using it for cooking. Keep the jar and the bill, and complain to the seller. You can also report it to your state food safety department or through FSSAI.
              </p>
              <p>
                If you want an oil you can check for yourself, Karijeeva is one ingredient, coconut, from our family farms in Tiptur and on the Uttara Kannada coast. We have sent {BRAND.batchesTested} batches to an independent food testing lab so far. Do the fridge test on it the day it arrives. You can <Link to="/products">order a jar here</Link>, and unopened jars are returnable within 7 days.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        { q: "How can I check the purity of coconut oil at home?", a: "Keep a little oil in a clear glass in the fridge (not the freezer) for 30 minutes. Pure coconut oil sets solid; other liquid oils mixed in stay as a separate layer. This is FSSAI's DART test and it only catches crude mixing." },
        { q: "Can the fridge test detect every kind of adulteration?", a: "No. Palm kernel oil also sets solid in the cold, so a mix with it can pass. Small amounts of another oil may not show a layer. For more certainty you need a lab report." },
        { q: "My coconut oil did not solidify in the fridge. Is it fake?", a: "Check that it stayed in for the full 30 minutes and that your fridge is cold enough. If it stays fully liquid or shows a clear liquid layer, it very likely contains another oil, so stop using it for cooking and contact the seller." },
        { q: "What should I look for on a coconut oil label?", a: "An FSSAI licence number, a batch number and packing date, one ingredient, and no words like refined, RBD or blended if you want unrefined oil. Be careful with brand names that closely imitate famous ones." },
        { q: "What does acid value mean in coconut oil?", a: "Acid value, or free fatty acids, shows how much the oil has started to break down. A lower figure usually means better raw material and fresher oil." },
        { q: "Does Karijeeva publish a lab report?", a: "Yes. Our lab reports page shows the latest quality report, which met the FSSAI standard for coconut oil on all six tests including acid value and the argemone oil test, and the nutrition panel per 100 g, each with its reference number." },
      ]}
      sources={[
        { label: "FSSAI DART: Detect Adulteration with Rapid Test", url: "https://eatrightindia.gov.in/dart/" },
        { label: "Summary of the FSSAI DART manual, Food Safety Helpline", url: "https://foodsafetyhelpline.com/fssai-publishes-manual-detect-adulteration-rapid-test-dart/" },
        { label: "Onmanorama: 74 coconut oil brands banned in Kerala, December 2018", url: "https://www.onmanorama.com/news/kerala/2018/12/18/adulteration-74-coconut-oil-brands-banned-in-kerala.html" },
        { label: "FoodNavigator Asia: FSSAI bans 14 coconut oil brands, 2019", url: "https://www.foodnavigator-asia.com/Article/2019/05/07/Coconut-oil-adulteration-FSSAI-bans-14-brands-in-India-as-new-detection-method-gains-traction/" },
        { label: "The South First: coconut oil adulteration in Kerala", url: "https://thesouthfirst.com/kerala/coconut-oil-adulteration-in-kerala-an-old-problem-in-new-bottles/" },
        { label: "FSSAI Food Products Standards, Chapter 2.2 Fats, oils and fat emulsions", url: "https://www.fssai.gov.in/upload/uploadfiles/files/Chapter%202_2%20(Fats,%20oils%20and%20fat%20emulsions).pdf" },
        { label: "FSSAI Labelling and Display compendium", url: "https://fssai.gov.in/upload/uploadfiles/files/Comp_Labelling.pdf" },
        { label: "Codex Standard for Named Vegetable Oils, FAO", url: "https://www.fao.org/4/y2774e/y2774e04.htm" },
      ]}
      related={[
        { label: "Cold pressed vs refined coconut oil", to: "/cold-pressed-vs-refined-coconut-oil" },
        { label: "Our lab report", to: "/lab-reports" },
        { label: "Cooking with coconut oil", to: "/cooking-with-coconut-oil" },
        { label: "Where our coconuts grow", to: "/the-farm" },
      ]}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Cooking guide                                                       */
/* ------------------------------------------------------------------ */

export function CookingGuide() {
  return (
    <GuidePage
      path="/cooking-with-coconut-oil"
      seoTitle="Cooking With Coconut Oil: Indian Kitchen Guide"
      seoDescription="Is coconut oil good for cooking Indian food? How to use it for tadka, thoran, dosa, appam, baking and shallow frying, what heat to use and how to store it."
      eyebrow="Cooking guide"
      title="How to cook with coconut oil in an Indian kitchen"
      tldr="Coconut oil works well for everyday Indian cooking at medium heat: tadka, poriyal and thoran, dosa and appam, shallow frying and some baking. It is mostly saturated fat, so it holds up to tempering, but use it in moderation and rotate it with other oils as ICMR NIN advises. Do not let it smoke, and do not reuse oil after deep frying."
      updated={UPDATED}
      testid="guide-cooking"
      sections={[
        {
          id: "good-for-cooking",
          heading: "Is coconut oil good for cooking Indian food?",
          body: (
            <>
              <p>
                For a large part of South India, coconut oil simply is cooking oil. Kerala avial, Mangalorean fish curry, Udupi style palya and a Tamil kootu all taste the way they do partly because of it. If a recipe grew up with coconut oil, it will taste most like itself with coconut oil.
              </p>
              <p>
                There is also a practical reason it suits tempering. Most of the fat in coconut oil is saturated, and saturated fat oxidises less under heat than oils high in polyunsaturated fat. In one lab comparison of ten oils heated to 180°C and above, coconut oil formed fewer polar compounds than refined seed oils, though that study was small and industry linked.
              </p>
              <p className="note">
                The honest other side: coconut oil is high in saturated fat. A 2020 meta analysis in Circulation found it raised LDL cholesterol by 10.47 mg/dL compared with non tropical vegetable oils. That is why we suggest using it for the dishes it suits, as one of the oils in your kitchen.
              </p>
            </>
          ),
        },
        {
          id: "dish-by-dish",
          heading: "How to use coconut oil dish by dish",
          body: (
            <>
              <Table
                caption="How to use coconut oil for common Indian dishes"
                head={["Dish", "How to use coconut oil", "Heat"]}
                rows={[
                  ["Tadka or tempering", "Warm 1 to 2 teaspoons, add mustard seeds, wait for them to splutter, then urad dal, curry leaves and red chilli.", "Medium"],
                  ["Poriyal, thoran, palya", "Temper as above, add the chopped vegetable, cook covered, finish with grated coconut.", "Medium, then low"],
                  ["Dosa and appam", "Wipe the tawa or appachatti with a few drops on a cloth or onion half. Drizzle a little around the edge of a dosa.", "Medium high for dosa, medium for appam"],
                  ["Shallow frying", "Use just enough to coat the pan for fish fry, cutlets or banana chips in small quantities.", "Medium"],
                  ["Finishing", "Pour a spoon of raw oil over avial, olan or a bowl of kanji just before serving.", "No heat"],
                  ["Baking", "Use melted coconut oil in place of butter or oil in cakes, cookies and banana bread, measure for measure.", "Oven as per recipe"],
                ]}
              />
              <p>
                For dishes from our kitchen, see the <Link to="/recipes">recipes page</Link>.
              </p>
            </>
          ),
        },
        {
          id: "heat",
          heading: "What heat should you use, and can you deep fry in coconut oil?",
          body: (
            <>
              <p>
                Cook with coconut oil on medium heat. For tadka, the oil is ready when a single mustard seed sizzles as soon as it touches the oil. If you see smoke, the pan is too hot: take it off the flame, let it cool a little and start again.
              </p>
              <p>
                Published smoke point figures for coconut oil vary widely from source to source, and we have not measured the smoke point of our own oil, so we will not quote a number. What matters in a home kitchen is simple: never let any oil smoke.
              </p>
              <p>
                We do not recommend coconut oil for deep frying. Research on heated cooking oils suggests coconut oil gives off far more volatile compounds once it goes past its smoke point, and a deep kadai of oil is hard to hold at medium heat. ICMR NIN also advises against reheating or reusing oil, so throw away oil left after deep frying rather than keeping it for the next day.
              </p>
            </>
          ),
        },
        {
          id: "how-much",
          heading: "How much coconut oil should you use?",
          body: (
            <>
              <p>
                The ICMR NIN Dietary Guidelines for Indians (2024) suggest about 20 to 30 g of visible fat per person per day for most sedentary adults. That covers all cooking oil, ghee and butter together, not coconut oil alone. Twenty to thirty grams is roughly four to six teaspoons.
              </p>
              <p>
                ICMR NIN also advises using a combination of oils, blended or by rotation, because any single oil alone is not preferred. In practice that could mean coconut oil for your South Indian tempering and poriyal, and groundnut, mustard, rice bran or sesame oil for other dishes. We would rather you use Karijeeva where it shines than use it for everything.
              </p>
              <ul>
                <li>Measure oil with a teaspoon for a week. Most people pour more than they think.</li>
                <li>Use a heavy, well seasoned pan so food needs less oil.</li>
                <li>Keep coconut oil for the dishes where its flavour matters.</li>
              </ul>
            </>
          ),
        },
        {
          id: "storage",
          heading: "Why does coconut oil turn white and solid?",
          body: (
            <>
              <p>
                Coconut oil turns solid and white below about 24°C. This is normal and has nothing to do with quality. In a Bengaluru winter, an air conditioned kitchen or a North Indian home, your oil may stay solid for weeks at a time. On a hot day in Mangaluru it will be clear liquid.
              </p>
              <p>
                In fact, setting solid in the cold is how the FSSAI fridge test tells pure coconut oil from oil mixed with other liquid oils. Our guide on <Link to="/how-to-check-coconut-oil-purity">checking coconut oil purity</Link> explains the test.
              </p>
              <h3>How to store and use solid oil</h3>
              <ul>
                <li>To melt it, stand the closed jar in a bowl of warm water for a few minutes. Do not put a jar on the flame.</li>
                <li>For cooking, you can also scoop solid oil straight into a warm pan with a dry spoon.</li>
                <li>Keep the cap tight and the jar away from the stove and direct sunlight.</li>
                <li>Always use a dry spoon. Water in the jar shortens shelf life.</li>
              </ul>
            </>
          ),
        },
        {
          id: "which-oil",
          heading: "Which coconut oil should you cook with?",
          body: (
            <>
              <p>
                For cooking where the coconut aroma matters, use unrefined cold pressed oil. Refined (RBD) oil has had its aroma stripped out by deodorising, so your avial will not smell the way it should. Our <Link to="/cold-pressed-vs-refined-coconut-oil">cold pressed vs refined guide</Link> explains why.
              </p>
              <p>
                Karijeeva is cold pressed from coconuts on our family farms in Tiptur and on the Karnataka coast, with one ingredient and nothing added. Every batch is tested at an independent food testing lab, and our latest nutrition report is on the <Link to="/lab-reports">lab reports page</Link>. If it sounds like the coconut oil your kitchen needs, you can <Link to="/products">choose a size here</Link>. Delivery is free above ₹998.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        { q: "Is coconut oil good for cooking Indian food?", a: "Yes, for dishes that grew up with it, such as tadka, thoran, poriyal, avial, dosa and appam, cooked at medium heat. It is high in saturated fat, so use it in moderation and rotate it with other oils, as ICMR NIN advises." },
        { q: "Can I use coconut oil for tadka?", a: "Yes, coconut oil is a traditional tempering oil across South India. Warm it on medium heat, add mustard seeds when a test seed sizzles, and never let the oil smoke." },
        { q: "Can I deep fry in coconut oil?", a: "We do not recommend it. Coconut oil is better suited to tempering and shallow frying at medium heat. Whatever oil you deep fry in, do not reuse it, as ICMR NIN advises." },
        { q: "What is the smoke point of cold pressed coconut oil?", a: "Published figures vary a lot between sources, and we have not measured our own oil, so we do not quote a number. Cook on medium heat and take the pan off the flame if you ever see smoke." },
        { q: "Why does coconut oil solidify in winter?", a: "Coconut oil turns solid and white below about 24°C. It is normal and does not mean the oil has gone bad. Stand the jar in warm water to melt it." },
        { q: "Can I use hair coconut oil for cooking?", a: "Only if the jar is sold as an edible oil with an FSSAI licence number. Some hair oils contain added ingredients. Karijeeva is edible cold pressed coconut oil with one ingredient." },
      ]}
      sources={[
        { label: "ICMR NIN Dietary Guidelines for Indians, 2024", url: "https://www.nin.res.in/dietaryguidelines/pdfjs/locale/DGI_2024.pdf" },
        { label: "ICMR NIN FAQs on using a combination of oils", url: "https://www.nin.res.in/faqs.html" },
        { label: "De Alzaa, Guillaume and Ravetti 2018, heating 10 cooking oils, Acta Scientific Nutritional Health", url: "https://actascientific.com/ASNH/pdf/ASNH-02-0083.pdf" },
        { label: "Emissions of volatile aldehydes from heated cooking oils, Food Chemistry", url: "https://www.sciencedirect.com/science/article/abs/pii/S0308814609011303" },
        { label: "Neelakantan, Seah and van Dam 2020, coconut oil and blood lipids, Circulation", url: "https://www.ahajournals.org/doi/10.1161/CIRCULATIONAHA.119.043052" },
        { label: "FSSAI DART: Detect Adulteration with Rapid Test", url: "https://eatrightindia.gov.in/dart/" },
      ]}
      related={[
        { label: "Cooking temperatures", to: "/coconut-oil-cooking-temperature" },
        { label: "What the studies say", to: "/coconut-oil-cooking-studies" },
        { label: "Coconut oil vs other oils", to: "/coconut-oil-vs-other-cooking-oils" },
        { label: "Recipes", to: "/recipes" },
        { label: "Cold pressed vs refined coconut oil", to: "/cold-pressed-vs-refined-coconut-oil" },
        { label: "How to check coconut oil purity", to: "/how-to-check-coconut-oil-purity" },
        { label: "Cold pressed coconut oil benefits", to: "/cold-pressed-coconut-oil-benefits" },
      ]}
    />
  );
}
